/**
 * WalletBadges — F-012 Phase 3.
 *
 * Reusable accessible status badges shared by `AdminClientiTable`,
 * `WalletSummaryCard` and `AuditTrailViewer`. WCAG: every badge carries a
 * text label + icon — never color alone.
 */
import {
  AlertCircle,
  CheckCircle2,
  XCircle,
  ShieldAlert,
  Clock,
  ShieldCheck,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  centsToNumber,
  walletState,
  WALLET_STATE_LABEL,
  actionLabel,
  type WalletState,
} from '../lib/format';
import type { FidoAction } from '@app/shared';

/* -------------------------------------------------------------------------- */
/* Availability badge: verde > 0, ambra = 0, rosso < 0                         */
/* -------------------------------------------------------------------------- */

export interface AvailabilityBadgeProps {
  /** Available amount in cents (string from API, BigInt or number). */
  availableCents: string | bigint | number;
  className?: string;
}

export function AvailabilityBadge({ availableCents, className }: AvailabilityBadgeProps) {
  const value = centsToNumber(availableCents);

  if (value > 0) {
    return (
      <span
        data-availability="positive"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800 dark:border-green-700 dark:bg-green-950 dark:text-green-200',
          className,
        )}
      >
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
        Disponibile
      </span>
    );
  }

  if (value === 0) {
    return (
      <span
        data-availability="zero"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200',
          className,
        )}
      >
        <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
        Al limite
      </span>
    );
  }

  return (
    <span
      data-availability="negative"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800 dark:border-red-700 dark:bg-red-950 dark:text-red-200',
        className,
      )}
    >
      <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
      Sconfinamento
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Wallet-state badge: blocked (rosso) / grace (ambra) / active (verde)       */
/* -------------------------------------------------------------------------- */

export interface WalletStateBadgeProps {
  walletBlocked: boolean;
  gracePeriodUntil: string | null;
  className?: string;
}

export function WalletStateBadge({
  walletBlocked,
  gracePeriodUntil,
  className,
}: WalletStateBadgeProps) {
  const state: WalletState = walletState({ walletBlocked, gracePeriodUntil });
  const label = WALLET_STATE_LABEL[state];

  const Icon = state === 'blocked' ? ShieldAlert : state === 'grace' ? Clock : ShieldCheck;
  const tone =
    state === 'blocked'
      ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-950 dark:text-red-200'
      : state === 'grace'
        ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200'
        : 'border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-950 dark:text-green-200';

  return (
    <span
      data-wallet-state={state}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium',
        tone,
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Action badge (audit trail): SET grigio / GRACE ambra / BLOCK rosso /        */
/* UNBLOCK verde                                                               */
/* -------------------------------------------------------------------------- */

export interface ActionBadgeProps {
  action: FidoAction;
  className?: string;
}

export function ActionBadge({ action, className }: ActionBadgeProps) {
  const variant =
    action === 'BLOCK'
      ? 'destructive'
      : action === 'UNBLOCK'
        ? 'default'
        : action === 'GRACE'
          ? 'secondary'
          : 'outline';

  return (
    <Badge data-action={action} variant={variant} className={className}>
      {actionLabel(action)}
    </Badge>
  );
}
